import {
  bpsToPercent,
  DirectIndexingMinInvestmentMap,
  DirectIndexingTypeShortText,
  formatPercentToDecimalPlaces,
  formatUsd,
  getDiPricingInfo,
} from "@frec-js/common";
import { DirectIndexType } from "@frec-js/common-client";
import { classNames } from "@frec-js/common-web";
import Decimal from "decimal.js";
import { FC } from "react";

import { useIsMobile } from "../hooks/useIsMobile";
import { DirectIndexingIcon } from "./DirectIndexingIcon";

const HIGHLIGHTED_INDICES = [
  DirectIndexType.Sp500,
  DirectIndexType.CrspLargeCap,
];

type IndicesTableProps = {
  availableIndexTypes?: DirectIndexType[];
};

export const IndicesTable: FC<IndicesTableProps> = ({
  availableIndexTypes,
}) => {
  if (!availableIndexTypes || availableIndexTypes.length === 0) {
    return null;
  }

  const enrichedAvailableIndices = availableIndexTypes
    .map((indexType) => {
      return {
        indexType,
        indexFee: bpsToPercent(getDiPricingInfo(indexType).directIndexBaseBPS),
        indexMinimum: DirectIndexingMinInvestmentMap[indexType],
      };
    })
    .sort((a, b) => a.indexFee.comparedTo(b.indexFee));

  return (
    <div className="border-frecDarkBeige mx-auto max-w-5xl rounded-xl bg-white px-4 py-2 lg:border lg:px-12 lg:py-6">
      <div
        role="table"
        aria-label="Direct indices fees and minimums"
        className="grid grid-cols-[1fr_minmax(0,10rem)_minmax(0,8rem)]"
      >
        {/* header */}
        <div className="text-12 md:text-16 text-frecDarkGray flex items-center py-6 font-medium uppercase">
          Index
        </div>
        <div className="dotted-v text-12 md:text-16 text-frecDarkGray flex items-center justify-center border-x px-2 text-center font-medium uppercase">
          Annual Fee
        </div>
        <div className="text-12 md:text-16 text-frecDarkGray flex items-center justify-center px-2 text-center font-medium uppercase">
          Minimum
        </div>
        {/* rows */}
        {enrichedAvailableIndices.map((e) => {
          return (
            <IndicesTableRowItem
              key={e.indexType}
              indexType={e.indexType}
              indexFee={e.indexFee}
              indexMinimum={e.indexMinimum}
              highlighted={HIGHLIGHTED_INDICES.includes(e.indexType)}
            />
          );
        })}
      </div>
    </div>
  );
};

const IndicesTableRowItem: FC<{
  indexType: DirectIndexType;
  indexFee: Decimal;
  indexMinimum: number;
  highlighted?: boolean;
}> = ({ indexType, indexFee, indexMinimum, highlighted }) => {
  const isMobile = useIsMobile();
  return (
    <>
      <IndicesTableDivider />
      <div className="py-6 pr-2">
        <div className="flex items-center gap-4">
          {isMobile !== undefined && (
            <DirectIndexingIcon
              className={isMobile ? "h-8 w-8" : ""}
              type={indexType}
              size={isMobile ? "sm" : "md"}
            />
          )}
          <span className="md:text-20">
            {DirectIndexingTypeShortText[indexType]}
          </span>
        </div>
      </div>
      <div
        className={classNames(
          "dotted-v md:text-20 flex items-center justify-center border-x",
          highlighted ? "bg-frecNeon font-medium" : "",
        )}
      >
        {formatPercentToDecimalPlaces(indexFee, 2)}
      </div>
      <div className="md:text-20 flex items-center justify-center">
        {formatUsd(indexMinimum)}
      </div>
    </>
  );
};

const IndicesTableDivider: FC = () => {
  return (
    <>
      <div className="dotted-h border-t"></div>
      <div className="dotted-h border-t"></div>
      <div className="dotted-h border-t"></div>
    </>
  );
};
